import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { object } from 'yup'
import { useFeatureFlags } from '@node-space/hooks'
import { CheckBoxProps } from '@node-space/storybook-components/dist/CheckBox'
import { Option as SelectOption } from '@node-space/storybook-components/dist/Select'
import { AddressFormKeyList } from 'components/AddressFormFields/constants'
import { AddressFormKey } from 'components/AddressFormFields/types'
import { ConfirmRadioOptions, ConfirmRadioValues } from 'constants/Forms'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useFormSetup } from 'hooks/forms/useFormSetup'
import { useYupFields } from 'hooks/forms/useYupFields'
import { useDateOfBirthInput } from 'hooks/useDateOfBirthInput'
import { FormFieldsType, FormSchemaType } from 'types/formField'
import {
  AccountContactFormKeyList,
  AccountContactFormKeys,
  AccountContactTypes,
  ControllerFormKeyList,
  ControllerInfoKeyList,
  FirstControllerInfoKey,
} from '../helpers/constants'
import {
  AccountContactData,
  AccountContactFormData,
  AccountContactFormKey,
  AccountContactType,
  ControllerFormKey,
  ControllerInfoKey,
} from '../helpers/types'
import { getIsValidPersonalIdNumber } from '../helpers/validation'
import { useAccountContactList } from './useAccountContactList'

export type AccountContactSchema = FormSchemaType<AccountContactFormKey>
export type AccountContactFields = FormFieldsType<AccountContactFormKey>

export type UseAccountContactFormArgs = {
  initialAccountContactData: AccountContactData
  countryOptions: SelectOption[]
  contactType: AccountContactType
}

export const useAccountContactForm = ({
  initialAccountContactData,
  countryOptions,
  contactType,
}: UseAccountContactFormArgs) => {
  const { t } = useTranslation()
  const { accountContacts } = useVerificationContext()
  const { isControllerRequired } = useAccountContactList(accountContacts?.list)
  const { enableDualNationality } = useFeatureFlags()
  const { formatDateOfBirthData } = useDateOfBirthInput()

  const {
    getYupField,
    getRequiredYupField,
    getEmailYupField,
    getRequiredRadioYupField,
    getOptionalCheckboxYupField,
  } = useYupFields()

  const isControllerCheckBox = (key: AccountContactFormKey) =>
    ControllerInfoKeyList?.includes(key as ControllerInfoKey)

  const formKeys: AccountContactFormKey[] = AccountContactFormKeyList?.filter(key => {
    if (ControllerFormKeyList?.includes(key as ControllerFormKey)) {
      const isDirectorType = contactType === AccountContactTypes.BUSINESS_DIRECTOR
      return isControllerRequired && isDirectorType
    }
    switch (key) {
      case AccountContactFormKeys.EMAIL_ADDRESS:
        return contactType === AccountContactTypes.ACCOUNT_REPRESENTATIVE
      case AccountContactFormKeys.SECOND_NATIONALITY:
        return !!enableDualNationality
      default:
        return true
    }
  })

  const getFieldSchema = (key: AccountContactFormKey) => {
    const label = t(`formFields.${key}`)
    const controllerLabel = t(`controller.${key}`)
    if (isControllerCheckBox(key)) {
      return getOptionalCheckboxYupField(controllerLabel)
    }
    switch (key) {
      case AccountContactFormKeys.EMAIL_ADDRESS:
        return getEmailYupField(label)
      case AccountContactFormKeys.ADDRESS_2:
      case AccountContactFormKeys.SECOND_NATIONALITY:
        return getYupField({ label })
      case AccountContactFormKeys.COUNTRY_CODE:
        return getRequiredYupField({
          label,
          requiredErrorMessage: t('formFields.countryIsRequired'),
        })
      case AccountContactFormKeys.PERSONAL_IDENTIFICATION_NUMBER:
        return getRequiredYupField({ label }).test({
          name: 'personalIdentificationNumberPattern',
          test: (value, context) => getIsValidPersonalIdNumber(value, context, t),
        })
      case AccountContactFormKeys.IS_CONTROLLER:
        return getRequiredRadioYupField(controllerLabel)
      default:
        return getRequiredYupField({ label })
    }
  }

  const schemaObj = formKeys?.reduce((schemaObj, key: AccountContactFormKey) => {
    schemaObj[key] = getFieldSchema(key)
    return schemaObj
  }, {} as AccountContactSchema)

  const getDefaultValue = (key: AccountContactFormKey) => {
    const { person, contactInfo, controllerInfo } = initialAccountContactData || {}
    if (isControllerCheckBox(key)) {
      return !!controllerInfo?.[key]
    }
    switch (key) {
      case AccountContactFormKeys.EMAIL_ADDRESS:
        return contactInfo?.emailAddress
      case AccountContactFormKeys.DATE_OF_BIRTH:
        return formatDateOfBirthData(person?.[key])
      case AccountContactFormKeys.IS_CONTROLLER:
        return !!controllerInfo ? ConfirmRadioValues.YES : ConfirmRadioValues.NO
      default:
        if (AddressFormKeyList?.includes(key as AddressFormKey)) {
          return person?.address?.[key]
        }
        return person?.[key]
    }
  }

  const defaultValues = formKeys?.reduce((valuesObj, key: AccountContactFormKey) => {
    valuesObj[key] = getDefaultValue(key) ?? ''
    return valuesObj
  }, {} as AccountContactFormData)

  const formSchema = object<AccountContactSchema>(schemaObj)
  const { form, formValues } = useFormSetup<AccountContactFormData>(formSchema, defaultValues)
  const formErrors = form?.formState?.errors || {}

  const { setInputField, setSelectField, setEmailField, setRadioField, setCheckboxField } =
    useFormFields<AccountContactFormKey>(formSchema, form, formValues)

  const getFieldProps = (key: AccountContactFormKey) => {
    if (isControllerCheckBox(key)) {
      return setCheckboxField({
        key,
        onChangeAdditional: value => !!value && form?.clearErrors(FirstControllerInfoKey),
      })
    }
    switch (key) {
      case AccountContactFormKeys.EMAIL_ADDRESS:
        return setEmailField({ key })
      case AccountContactFormKeys.DATE_OF_BIRTH:
        return {} // handled as custom control
      case AccountContactFormKeys.COUNTRY_CODE:
      case AccountContactFormKeys.NATIONALITY:
      case AccountContactFormKeys.SECOND_NATIONALITY:
        return setSelectField({
          key,
          options: countryOptions,
          placeholder: t('formFields.chooseCountry'),
          showLabel: key !== AccountContactFormKeys.COUNTRY_CODE,
        })
      case AccountContactFormKeys.PERSONAL_IDENTIFICATION_NUMBER:
        return setInputField({
          key,
          placeholder: t('formFields.enterNumber'),
        })
      case AccountContactFormKeys.IS_CONTROLLER:
        return setRadioField({
          key,
          options: ConfirmRadioOptions?.map(({ label, value }) => ({ label: t(label), value })),
          onChangeAdditional: value => {
            value !== ConfirmRadioValues.YES && form?.clearErrors(FirstControllerInfoKey)
          },
        })
      default:
        return setInputField({ key, useLabelAsPlaceholder: true, showLabel: false })
    }
  }

  const formFields = formKeys?.reduce((fieldsObj, key: AccountContactFormKey) => {
    fieldsObj[key] = getFieldProps(key)
    return fieldsObj
  }, {} as AccountContactFields)

  const getIsControllerCheckBoxesValid = () => {
    const hasCheckedControllerInfo = ControllerInfoKeyList?.some(
      key => (formFields?.[key] as CheckBoxProps)?.value
    )
    if (hasCheckedControllerInfo) return true
    if (!hasCheckedControllerInfo) {
      form?.setError(FirstControllerInfoKey, { message: t('controller.optionRequiredError') })
      return false
    }
  }

  const resetForm = () => {
    form?.reset(defaultValues)
  }

  useEffect(() => {
    if (!enableDualNationality && formErrors?.nationalityCountryCode) {
      form?.clearErrors('nationalityCountryCode')
    }
  }, [enableDualNationality, formErrors?.nationalityCountryCode])

  return { form, formValues, formFields, formErrors, resetForm, getIsControllerCheckBoxesValid }
}
